/**
 * ============================================================================
 * packDropService.ts - PACK DROP
 * ============================================================================
 * One free pack twice a week, unlocked by playing a hand. Prizes are
 * PlayCoins only - deliberately, so a pure-chance opening never trades in
 * redeemable currency.
 *
 * Everything authoritative lives on the server: the cycle, the activity
 * gate, the prize roll and the wallet credit. This file only moves data and
 * mirrors the prize table so the reel has something to render while it
 * spins - the prize that lands always comes from the server's reply.
 * ============================================================================
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export interface PackPrize {
  id: string;
  label: string;
  value: number;
  tier: 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary' | 'Cash' | 'Jackpot';
  color: string;
  weight: number;
  /** pkcash prizes are PSS Coins with 1x playthrough; default playcoins. */
  kind?: 'playcoins' | 'pkcash';
}

export interface PackStatus {
  enabled: boolean;
  cycleId: string;
  endsAt: number;
  playedThisCycle: boolean;
  opened: boolean;
  prize: PackPrize | null;
  prizes: PackPrize[];
}

export interface PackOpenResult {
  success: boolean;
  alreadyOpened?: boolean;
  prize?: PackPrize;
  endsAt?: number;
  error?: string;
}

/**
 * Fallback strip for the reel. Only ever used for the blur of prizes flying
 * past before the server answers - never to decide an outcome.
 */
export const PACK_PRIZES_FALLBACK: PackPrize[] = [
  { id: 'pc_1000',   label: '1,000',   value: 1000,   tier: 'Common',    color: '#5C7A8A', weight: 30 },
  { id: 'pc_2500',   label: '2,500',   value: 2500,   tier: 'Common',    color: '#5C7A8A', weight: 26 },
  { id: 'pc_5000',   label: '5,000',   value: 5000,   tier: 'Uncommon',  color: '#22C55E', weight: 20 },
  { id: 'pc_10000',  label: '10,000',  value: 10000,  tier: 'Uncommon',  color: '#22C55E', weight: 14 },
  { id: 'pc_25000',  label: '25,000',  value: 25000,  tier: 'Rare',      color: '#38BDF8', weight: 7 },
  { id: 'pc_50000',  label: '50,000',  value: 50000,  tier: 'Epic',      color: '#A855F7', weight: 2 },
  { id: 'pc_100000', label: '100,000', value: 100000, tier: 'Legendary', color: '#FFD700', weight: 0.5 },
  { id: 'pss_1',     label: '1',       value: 1,      tier: 'Cash',      color: '#22C55E', weight: 4,    kind: 'pkcash' },
  { id: 'pss_5',     label: '5',       value: 5,      tier: 'Cash',      color: '#FFD700', weight: 0.5,  kind: 'pkcash' },
  { id: 'pss_50',    label: '50',      value: 50,     tier: 'Jackpot',   color: '#FF5C8A', weight: 0.04, kind: 'pkcash' },
];

export async function getPackStatus(): Promise<PackStatus | null> {
  try {
    const fn = httpsCallable<Record<string, never>, PackStatus>(functions, 'packDropStatus');
    const r = await fn({});
    return r.data;
  } catch (err) {
    console.error('[PackDrop] status error:', err);
    return null;
  }
}

export async function openPack(): Promise<PackOpenResult> {
  try {
    const fn = httpsCallable<Record<string, never>, PackOpenResult>(functions, 'openPackDrop');
    const r = await fn({});
    return r.data;
  } catch (err: any) {
    // The callable throws a readable message for the gate and the kill
    // switch - surface it rather than a generic failure.
    return { success: false, error: err?.message || 'Could not open your pack' };
  }
}

/** "13d 22h" / "4h 12m" - the countdown to the next drop. */
export function formatUntil(ts: number | null | undefined): string {
  if (!ts) return '';
  const ms = ts - Date.now();
  if (ms <= 0) return 'now';
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hrs = Math.floor((mins % 1440) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  return `${hrs}h ${mins % 60}m`;
}

const packDropService = { getPackStatus, openPack, formatUntil, PACK_PRIZES_FALLBACK };
export default packDropService;
